/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { Box, Grid, Skeleton, Typography, useMediaQuery, useTheme } from "@mui/material";
import CustomRightImage from "@components/CustomRightImagev2";
import icon_check from "@assets/img/check_black.svg";
import { ButtonTypeSelectedCustom } from "@components/ButtonTypeSelectedCustom";
import FormByOneVehicle from "@components/arredamiento/FormByOneVehicle";
import FormByMultiVehicles from "@components/arredamiento/FormByMultiVehicles";
import { useLazyGetTaxSystemsQuery } from "@api/catalogs";
import { LoanCondiciones, TaxSystem } from "@interfaces/index";
import { useLazyGetConditionsByIdQuery } from "@api/applications";
import { useSelector } from "react-redux";
import { RootState } from "@store/store";

interface MarketStructure {
  value: number;
  label: string;
}

export const SeleccionVehiculos = () => {
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const isShortScreen = useMediaQuery("(max-height:820px)");

  const application = useSelector((state: RootState) => state.register.application);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [vehicleNumber, setVehicleNumber] = useState<string>("uno");

  const [getTaxSystems] = useLazyGetTaxSystemsQuery();
  const [getConditions] = useLazyGetConditionsByIdQuery();

  const [taxSystems, setTaxSystems] = useState<TaxSystem | null>(null);
  const [termMarket, setTermMarket] = useState<number[]>([]);
  const [residualPercentageMarket, setResidualPercentageMarket] = useState<MarketStructure[]>([]);
  const [advancePercentageMarket, setAdvancePercentageMarket] = useState<MarketStructure[]>([]);
  const [condition, setCondition] = useState<LoanCondiciones | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);

        const responseTax = await getTaxSystems(application.type).unwrap();
        const responseConditions = await getConditions(application.id).unwrap();

        setTaxSystems(responseTax);
        const activeCondition = responseConditions.find((item) => item.active);
        if (activeCondition) {
          setCondition(activeCondition);
        }
      } catch (error) {
        console.log("Ha ocurrido un error", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const typedCfg = taxSystems;
    const min = Number(typedCfg?.min_term ?? 12);
    const max = Number(typedCfg?.max_term ?? 60);
    const jump = Number(typedCfg?.jump_term ?? 12);

    const minResidual = Number(typedCfg?.min_residual_percentage ?? 5);
    const maxResidual = Number(typedCfg?.max_residual_percentage ?? 40);
    const jumpResidual = Number(typedCfg?.jump_residual_percentage ?? 5);

    const minAdvance = Number(typedCfg?.min_advance_percentage ?? 5);
    const maxAdvance = Number(typedCfg?.max_advance_percentage ?? 40);
    const jumpAdvance = Number(typedCfg?.jump_advance_percentage ?? 5);

    const list: number[] = [];
    const listResidual: MarketStructure[] = [];
    const listAdvance: MarketStructure[] = [];

    if (jump > 0 && min > 0 && max >= min) {
      for (let t = min; t <= max; t += jump) {
        list.push(t);
      }
      if (list.length && list[list.length - 1] !== max) {
        list.push(max);
      }
    }

    if (jumpResidual > 0 && minResidual > 0 && maxResidual >= minResidual) {
      for (let r = minResidual; r <= maxResidual; r += jumpResidual) {
        listResidual.push({ value: r, label: `${r}%` });
      }
      if (listResidual.length && listResidual[listResidual.length - 1].value !== maxResidual) {
        listResidual.push({ value: maxResidual, label: `${maxResidual}%` });
      }
    }

    if (jumpAdvance > 0 && minAdvance > 0 && maxAdvance >= minAdvance) {
      for (let r = minAdvance; r <= maxAdvance; r += jumpAdvance) {
        listAdvance.push({ value: r, label: `${r}%` });
      }
      if (listAdvance.length && listAdvance[listAdvance.length - 1].value !== maxAdvance) {
        listAdvance.push({ value: maxAdvance, label: `${maxAdvance}%` });
      }
    }

    setTermMarket(list);
    setResidualPercentageMarket(listResidual);
    setAdvancePercentageMarket(listAdvance);
  }, [taxSystems]);

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8}>
        <Box maxWidth="sm" mx="auto" mt={4} px={isMobileOrTablet ? 0 : 2}>
          <Typography textAlign={"center"} variant={"h4"} fontWeight={700} color={theme?.palette?.primary?.dark}>
            Selecciona el o los vehículos a arrendar
          </Typography>

          <Typography
            textAlign={"center"}
            variant={"body1"}
            fontWeight={400}
            color={theme?.palette?.primary?.dark}
            mt={4}
          >
            Selecciona el número de vehículos
          </Typography>

          <Grid container spacing={isMobileOrTablet ? 2 : 2} mt={1} mr={isMobileOrTablet ? 1 : 0}>
            <Grid item xs={6} md={6} display="flex" justifyContent={"flex-end"}>
              {!isLoading ? (
                <ButtonTypeSelectedCustom
                  title="Un vehículo"
                  onClick={() => setVehicleNumber("uno")}
                  isSelected={vehicleNumber === "uno"}
                  isMovile={isMobileOrTablet}
                  height={isShortScreen ? "60px" : "70px"}
                  heightMoviles="60px"
                  width={isShortScreen ? "180px" : "190px"}
                  widthMoviles="160px"
                  icon={icon_check}
                  width_icon="22px"
                  height_icon="22px"
                  font_size={isShortScreen ? "14px" : "14px"}
                  top_icon="-12px"
                  font_size_movil="11px"
                  font_weight={600}
                  bgColor={theme?.palette?.primary.base}
                  borderColor={theme?.palette?.primary?.light}
                  bgColorActive={theme?.palette?.primary?.light}
                  hoverBgColor={theme?.palette?.primary?.light}
                  hoverTextColor={theme?.palette?.primary?.dark}
                  textColor={theme?.palette?.primary?.dark}
                  textColorActive={theme?.palette?.primary?.dark}
                />
              ) : (
                <Skeleton
                  variant="rectangular"
                  animation="wave"
                  width={isMobileOrTablet ? 150 : 190}
                  height={isMobileOrTablet ? 60 : 70}
                />
              )}
            </Grid>
            <Grid item xs={6} md={6} display="flex" justifyContent={"flex-start"}>
              {!isLoading ? (
                <ButtonTypeSelectedCustom
                  title="Más de un vehículo"
                  onClick={() => setVehicleNumber("mas_de_uno")}
                  isSelected={vehicleNumber === "mas_de_uno"}
                  isMovile={isMobileOrTablet}
                  height={isShortScreen ? "60px" : "70px"}
                  heightMoviles="60px"
                  width={isShortScreen ? "180px" : "200px"}
                  widthMoviles="160px"
                  icon={icon_check}
                  width_icon="22px"
                  height_icon="22px"
                  font_size={isShortScreen ? "14px" : "14px"}
                  top_icon="-12px"
                  font_size_movil="11px"
                  font_weight={600}
                  bgColor={theme?.palette?.primary?.base}
                  borderColor={theme?.palette?.primary?.light}
                  bgColorActive={theme?.palette?.primary?.light}
                  hoverBgColor={theme?.palette?.primary?.light}
                  hoverTextColor={theme?.palette?.primary?.dark}
                  textColor={theme?.palette?.primary?.dark}
                  textColorActive={theme?.palette?.primary?.dark}
                />
              ) : (
                <Skeleton
                  variant="rectangular"
                  animation="wave"
                  width={isMobileOrTablet ? 150 : 190}
                  height={isMobileOrTablet ? 60 : 70}
                />
              )}
            </Grid>
          </Grid>

          {vehicleNumber === "uno" && (
            <>
              <Box mt={4}>
                <FormByOneVehicle
                  isLoading={isLoading}
                  taxConfig={taxSystems}
                  termMarket={termMarket}
                  residualPercentageMarket={residualPercentageMarket}
                  advancePercentageMarket={advancePercentageMarket}
                  maxAmoutSelected={condition?.selected_amount}
                  conditions={condition}
                />
              </Box>
            </>
          )}

          {vehicleNumber === "mas_de_uno" && (
            <>
              <Box mt={4}>
                <FormByMultiVehicles
                  isLoading={isLoading}
                  taxConfig={taxSystems}
                  termMarket={termMarket}
                  residualPercentageMarket={residualPercentageMarket}
                  advancePercentageMarket={advancePercentageMarket}
                  maxAmoutSelected={condition?.selected_amount}
                  conditions={condition}
                />
              </Box>
            </>
          )}

          <Box mb={4} mt={4} p={1} sx={{ borderRadius: "5px", backgroundColor: theme?.palette?.primary?.base }}>
            <Typography fontSize={"11px"} fontWeight={400} color={theme?.palette?.primary?.dark}>
              Esta simulación es únicamente informativa. Las condiciones podrán ajustarse y cambiar durante el proceso
              según el valor final del vehículo, seguros, comisiones, descuentos aplicables y el resultado de la
              evaluación de crédito. Los montos mostrados no incluyen IVA.
            </Typography>
          </Box>
        </Box>
      </Grid>
      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}
    </Grid>
  );
};
